import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-job-materials',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-24" style="background:#F7FBFD;">
      <!-- Header -->
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex items-center gap-3"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <a [routerLink]="['/job', jobId]" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </a>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'jobRecord' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ 'jobMaterials' | t }}</h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">{{ materials().length }}</span>
      </header>

      <main class="w-full max-w-3xl mx-auto px-3.5 pt-4 space-y-4 sm:px-5">
        <!-- Current Materials List -->
        <div class="space-y-3">
          <div class="flex items-center justify-between px-1">
            <h2 class="text-xs font-black text-slate-400 uppercase tracking-widest">Logged Usage</h2>
            <span class="text-[10px] font-semibold text-[#1A6FA6] bg-white px-2 py-0.5 rounded-full border border-slate-100 shadow-sm">{{ materials().length }} Items</span>
          </div>
          
          @if (isLoading()) {
            <div class="py-12 flex flex-col items-center justify-center text-slate-400">
              <i class="ti ti-loader animate-spin text-3xl mb-2"></i>
              <p class="text-sm font-bold">Fetching usage logs...</p>
            </div>
          } @else if (materials().length === 0) {
            <div class="bg-white rounded-2xl p-10 text-center border border-dashed border-slate-200 shadow-sm">
              <div class="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                <i class="ti ti-box text-4xl text-slate-200"></i>
              </div>
              <p class="text-slate-700 font-semibold mb-1">{{ 'noMaterialsLogged' | t }}</p>
              <p class="text-sm text-slate-400">Add the first material used on this job.</p>
            </div>
          } @else {
            <div class="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
              @for (m of materials(); track m.id) {
                <div class="bg-white rounded-2xl p-3 shadow-sm border border-slate-100 grid grid-cols-[40px_minmax(0,1fr)_auto] items-start gap-3 sm:p-4 sm:grid-cols-[44px_minmax(0,1fr)_auto]">
                  <div class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-sky-50 text-[#1A6FA6] flex items-center justify-center shrink-0 border border-sky-100 overflow-hidden">
                    <i class="ti ti-package text-[20px] leading-none"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <h3 class="font-semibold text-slate-900 break-words text-[15px] leading-tight">{{m.item_name}}</h3>
                    <div class="flex flex-wrap items-center gap-1.5 mt-1">
                       <span class="text-[10px] font-semibold text-[#1A6FA6] bg-sky-50 px-1.5 py-0.5 rounded border border-sky-100">{{m.quantity}} {{m.unit}}</span>
                       @if (m.material_id) {
                         <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter italic">Library Part</span>
                       } @else {
                         <span class="text-[9px] font-bold text-slate-400 uppercase tracking-tighter italic">Manual Part</span>
                       }
                       <span class="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border" [ngClass]="readinessClass(m.readiness_status)">{{ m.readiness_status || 'Used' }}</span>
                    </div>
                    @if (m.readiness_note) {
                      <p class="text-xs text-slate-500 mt-1 truncate">{{ m.readiness_note }}</p>
                    }
                    @if (+m.sell_price > 0) {
                      <p class="text-xs font-bold text-slate-500 mt-1">{{m.sell_price | currency:'NZD'}} each · {{m.quantity * m.sell_price | currency:'NZD'}} total</p>
                    }
                  </div>
                  <div class="flex items-center gap-0.5 self-center">
                    <select [ngModel]="m.readiness_status || 'Used'" (ngModelChange)="saveReadiness(m, $event)" aria-label="Material readiness" class="w-[88px] sm:w-28 h-9 rounded-xl border border-slate-200 bg-slate-50 px-2 text-[11px] font-semibold text-slate-600 outline-none">
                      @for (status of readinessStatuses; track status) {
                        <option [value]="status">{{ status }}</option>
                      }
                    </select>
                    <button (click)="removeItem(m.id)" aria-label="Remove material" class="w-9 h-9 rounded-xl text-slate-300 active:text-red-500 active:bg-red-50 transition-colors flex items-center justify-center">
                      <i class="ti ti-trash text-[18px] leading-none"></i>
                    </button>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Add Feature -->
        <div class="bg-white rounded-2xl p-3.5 shadow-sm border border-slate-100 space-y-3">
           <h3 class="font-semibold text-slate-800 text-sm">{{ 'newEntry' | t }}</h3>
           <div class="grid grid-cols-2 gap-3">
              <button (click)="openPicker()" class="h-16 rounded-xl bg-[#F1F9FE] border border-[#E4F1FA] flex flex-col items-center justify-center gap-1 active:bg-[#E4F1FA] transition-all group">
                <i class="ti ti-search text-xl leading-none text-[#2B9AD2]"></i>
                <span class="text-[10px] font-semibold text-[#1A6FA6] uppercase">{{ 'search' | t }}</span>
              </button>
              <button (click)="startManualEntry()" class="h-16 rounded-xl bg-slate-50 border border-slate-100 flex flex-col items-center justify-center gap-1 active:bg-slate-100 transition-all group">
                <i class="ti ti-pencil text-xl leading-none text-slate-400"></i>
                <span class="text-[10px] font-semibold text-slate-400 uppercase">{{ 'addToJob' | t }}</span>
              </button>
           </div>
        </div>
      </main>

      <!-- Search & Add Modal -->
      @if (showPicker() || isManual()) {
        <div class="fixed inset-0 bg-[#0b3146]/94 backdrop-blur-sm z-[100] flex flex-col animate-in fade-in duration-200 overflow-hidden">
          
          <div class="bg-white px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-4 rounded-b-[28px] shadow-xl shrink-0 sm:px-5">
             <div class="flex items-center justify-between gap-3 mb-4">
                <h2 class="text-xl font-bold text-[#0b3146] tracking-tight">{{ isManual() ? 'Manual Entry' : 'Pick Material' }}</h2>
                <button (click)="closeModal()" aria-label="Close material picker" class="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                  <i class="ti ti-x text-[20px] leading-none"></i>
                </button>
             </div>

             @if (!selectedItem() && !isManual()) {
               <!-- Search View -->
               <div class="relative">
                  <i class="ti ti-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[18px] leading-none"></i>
                  <input type="text" placeholder="Search library items..." 
                         [(ngModel)]="searchQuery" (input)="onSearch()"
                         class="w-full h-12 pl-11 pr-4 bg-slate-50 border border-slate-100 rounded-xl font-medium text-base focus:bg-white focus:border-[#2B9AD2] focus:ring-2 focus:ring-[#2B9AD2]/10 outline-none transition-all">
               </div>
             } @else {
               <!-- Selection Details View -->
               <div class="space-y-4 animate-in zoom-in-95 duration-200">
                  @if (selectedItem()) {
                    <div class="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100">
                       <div class="w-11 h-11 rounded-xl overflow-hidden border border-white shrink-0">
                          <img [src]="selectedItem().photo_url || defaultPhoto" class="w-full h-full object-cover">
                       </div>
                       <div class="flex-1 min-w-0">
                          <h4 class="font-bold text-slate-900 truncate">{{selectedItem().name}}</h4>
                          <p class="text-xs text-slate-400 font-mono tracking-tighter">{{selectedItem().serial_no}}</p>
                       </div>
                    </div>
                  } @else {
                    <input type="text" [(ngModel)]="manualName" placeholder="What part is this?"
                           class="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl font-semibold text-base focus:bg-white focus:border-[#2B9AD2] outline-none">
                  }

                  <div class="flex gap-3">
                     <div class="flex-1">
                       <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Quantity</p>
                       <div class="flex items-center gap-1 bg-slate-50 rounded-xl h-12 p-1 border border-slate-100">
                          <button (click)="qty.set(qty() > 1 ? qty() - 1 : 1)" class="w-10 h-full rounded-lg active:bg-white transition-all text-slate-400"><i class="ti ti-minus"></i></button>
                          <input type="number" [(ngModel)]="qty" class="w-12 flex-1 text-center font-bold text-base bg-transparent border-none outline-none text-[#0b3146]">
                          <button (click)="qty.set(qty() + 1)" class="w-10 h-full rounded-lg active:bg-white transition-all text-slate-400"><i class="ti ti-plus"></i></button>
                       </div>
                     </div>
                     <div class="w-28">
                       <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Unit</p>
                       <select [(ngModel)]="unit" class="w-full h-12 px-3 bg-slate-100 border-none rounded-xl font-semibold text-sm text-[#0b3146] appearance-none text-center">
                          @for (u of units; track u) { <option [value]="u">{{u}}</option> }
                       </select>
                     </div>
                  </div>

                  <div>
                    <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Sell Price</p>
                    <div class="relative">
                      <span class="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 font-black">$</span>
                      <input type="number" min="0" step="0.01" [(ngModel)]="sellPrice"
                             class="w-full h-12 pl-10 pr-4 bg-slate-50 border border-slate-100 rounded-xl font-semibold text-base text-[#0b3146] focus:bg-white focus:border-[#2B9AD2] outline-none">
                    </div>
                  </div>

                  <div>
                    <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Readiness</p>
                    <select [(ngModel)]="readinessStatus" class="w-full h-12 px-4 bg-slate-50 border border-slate-100 rounded-xl font-semibold text-sm text-[#0b3146] outline-none">
                      @for (status of readinessStatuses; track status) {
                        <option [value]="status">{{ status }}</option>
                      }
                    </select>
                  </div>

                  @if (readinessStatus === 'Required' || readinessStatus === 'Unavailable' || readinessStatus === 'Supplier Order Needed') {
                    <div>
                      <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1.5">Readiness Note</p>
                      <textarea [(ngModel)]="readinessNote" rows="3" placeholder="Example: supplier order needed, collect before booking..."
                                class="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl font-semibold text-sm text-[#0b3146] outline-none resize-none"></textarea>
                    </div>
                  }

                  <button (click)="confirmAdd()" [disabled]="(isManual() && !manualName.trim()) || sellPrice() < 0 || readinessNoteRequired()"
                          class="w-full h-12 rounded-xl bg-[#2B9AD2] text-white font-bold text-sm shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-30">
                     <i class="ti ti-check text-lg leading-none"></i>
                     LOG USAGE
                  </button>
               </div>
             }
          </div>

          <!-- Bottom Results -->
          @if (!selectedItem() && !isManual()) {
            <div class="flex-1 min-h-0 overflow-auto px-4 pt-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
              @if (isSearching()) {
                <div class="min-h-[240px] flex flex-col items-center justify-center text-white/55">
                  <i class="ti ti-loader animate-spin text-2xl leading-none mb-3"></i>
                  <p class="text-sm font-semibold">Searching library...</p>
                </div>
              } @else {
                <div class="space-y-3">
                  @for (m of libraryResults(); track m.id) {
                    <button type="button" (click)="selectItem(m)" class="w-full text-left bg-white/10 rounded-2xl p-3 flex items-center gap-3 border border-white/10 active:bg-white/20 transition-all animate-in fade-in slide-in-from-bottom-2">
                      <div class="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0 border border-white/5 overflow-hidden">
                        <img [src]="m.photo_url || defaultPhoto" class="w-full h-full object-cover">
                      </div>
                      <div class="flex-1 min-w-0">
                         <h4 class="font-semibold text-white truncate text-[15px] leading-tight">{{m.name}}</h4>
                         <p class="text-xs text-white/30 font-mono tracking-widest uppercase mt-0.5">{{m.serial_no}} · {{m.unit}}</p>
                      </div>
                      <div class="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center border border-white/10 shrink-0">
                        <i class="ti ti-chevron-right text-base leading-none"></i>
                      </div>
                    </button>
                  }
                  @if (libraryResults().length === 0) {
                    <div class="min-h-[260px] flex flex-col items-center justify-center text-center text-white/45">
                      <span class="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-3 overflow-hidden">
                        <i class="ti ti-package-off text-[28px] leading-none"></i>
                      </span>
                      <p class="font-semibold tracking-wider uppercase text-[11px]">No library parts found</p>
                      <p class="mt-1.5 text-xs text-white/35 normal-case tracking-normal">Try another search or add a manual part.</p>
                    </div>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  `
})
export class JobMaterialsComponent implements OnInit {
  api = inject(ApiService);
  route = inject(ActivatedRoute);

  jobId: string = '';
  materials = signal<any[]>([]);
  isLoading = signal(true);

  // Modal State
  showPicker = signal(false);
  isManual = signal(false);
  selectedItem = signal<any>(null);
  
  // Input State
  searchQuery = '';
  isSearching = signal(false);
  libraryResults = signal<any[]>([]);
  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  qty = signal(1);
  sellPrice = signal(0);
  unit = 'EA';
  manualName = '';
  readinessStatuses = ['Estimated', 'Required', 'Ready', 'Used', 'Unavailable', 'Supplier Order Needed'];
  readinessStatus = 'Used';
  readinessNote = '';
  
  units = ['EA', 'PR', 'EACH', 'MET', 'LEN', 'PACK', 'PARK', 'ROLL', 'COIL', 'SHT', 'BOX', 'JAR', 'SET', 'BAG', 'KIT', 'PKT', 'DAY'];
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.jobId = params.get('id') || '';
      if (this.jobId) {
        this.loadJobMaterials();
      }
    });
  }

  loadJobMaterials() {
    this.isLoading.set(true);
    this.api.getJob(this.jobId).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.materials.set(res.data.materials || []);
        }
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  openPicker() {
    this.showPicker.set(true);
    this.onSearch();
  }

  startManualEntry() {
    this.isManual.set(true);
    this.manualName = '';
    this.qty.set(1);
    this.sellPrice.set(0);
    this.unit = 'EA';
    this.readinessStatus = 'Used';
    this.readinessNote = '';
  }

  closeModal() {
    this.showPicker.set(false);
    this.isManual.set(false);
    this.selectedItem.set(null);
    this.sellPrice.set(0);
    this.readinessStatus = 'Used';
    this.readinessNote = '';
  }

  onSearch() {
    const term = this.searchQuery.trim();
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (term.length > 0 && term.length < 2) {
      this.libraryResults.set([]);
      this.isSearching.set(false);
      return;
    }

    this.isSearching.set(true);
    this.searchTimer = setTimeout(() => {
      this.api.getMaterials({ search: term, limit: 50 }).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.libraryResults.set(res.data.materials || []);
          }
          this.isSearching.set(false);
        },
        error: () => this.isSearching.set(false)
      });
    }, 250);
  }

  selectItem(item: any) {
    this.selectedItem.set(item);
    this.qty.set(1);
    this.unit = item.unit || 'EA';
    this.sellPrice.set(Number(item.max_sell_price || item.sell_price || 0));
    this.readinessStatus = 'Used';
    this.readinessNote = '';
  }

  confirmAdd() {
    const itemName = this.selectedItem() ? this.selectedItem().name : this.manualName.trim();
    if (!itemName) {
      showToast('error', 'Material name is required');
      return;
    }
    if (this.sellPrice() < 0) {
      showToast('error', 'Sell price cannot be negative');
      return;
    }
    if (this.readinessNoteRequired()) {
      showToast('error', 'Add a note for unavailable or supplier-order parts.');
      return;
    }

    const payload = {
      job_id: this.jobId,
      material_id: this.selectedItem()?.id || null,
      item_name: itemName,
      quantity: this.qty(),
      unit: this.unit,
      cost: this.selectedItem() ? this.selectedItem().cost_price : 0,
      sell_price: this.sellPrice(),
      readiness_status: this.readinessStatus,
      readiness_note: this.readinessNote.trim()
    };

    this.api.addJobMaterial(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', `${payload.item_name} logged`);
          this.loadJobMaterials();
          this.closeModal();
        } else {
          showToast('error', res.message);
        }
      },
      error: () => showToast('error', 'API failure logging material')
    });
  }

  removeItem(id: number) {
    this.api.removeJobMaterial(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Material removed');
          this.loadJobMaterials();
        }
      }
    });
  }

  saveReadiness(material: any, readinessStatus: string) {
    if (!material) return;
    let note = material.readiness_note || '';
    if (this.readinessNoteRequired(readinessStatus, note)) {
      const entered = window.prompt('Add a short note for this parts issue:', note);
      if (!entered || entered.trim().length < 5) {
        showToast('error', 'A note is required for unavailable or supplier-order parts.');
        return;
      }
      note = entered.trim();
    }
    this.api.updateJobMaterialReadiness({
      id: material.id,
      readiness_status: readinessStatus,
      readiness_note: note,
      required_by: material.required_by || null
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Readiness updated');
          this.loadJobMaterials();
        } else {
          showToast('error', res.message || 'Could not update readiness');
        }
      },
      error: (err: any) => showToast('error', err?.error?.message || 'Could not update readiness')
    });
  }

  readinessNoteRequired(status = this.readinessStatus, note = this.readinessNote): boolean {
    return ['Unavailable', 'Supplier Order Needed'].includes(status) && note.trim().length < 5;
  }

  readinessClass(status: string): string {
    if (status === 'Ready' || status === 'Used') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (status === 'Required' || status === 'Estimated') return 'bg-amber-50 text-amber-700 border-amber-100';
    if (status === 'Unavailable' || status === 'Supplier Order Needed') return 'bg-rose-50 text-rose-700 border-rose-100';
    return 'bg-slate-50 text-slate-500 border-slate-100';
  }
}
